import { IcoArrowDownFilled, IcoArrowUpFilled } from '../../../component';
import {
  labelFormatterMoneyAsInteger,
  labelFormatterNumber,
  unmaskAbsoluteValue,
} from '../../../shared/formatters/formatters';
import { DisplayChartsKeys } from '../charts/types';
import {
  BigNumbersData,
  MarketCycleBN,
  MarketCycleBNData,
  PrimaryMarketBN,
  PrimaryMarketGeneralBN,
  SecondaryMarketBN,
  SecondaryMarketGeneralBN,
  TransactionedMarketBN,
} from './types';

export const useBigNumbersData = (
  data: BigNumbersData,
  displayCharts: DisplayChartsKeys,
  skipFormatting: boolean
): {
  formattedData:
    | Record<string, string | number | JSX.Element>
    | null
    | BigNumbersData;
  periods: Record<string, string | number> | null;
} => {
  let formattedData = null;
  let periods = null;
  if (skipFormatting) {
    formattedData = data;
    return { formattedData, periods };
  }
  switch (displayCharts) {
    case 'primaryMarket':
      formattedData = formatPrimaryMarket(data as PrimaryMarketBN);
      break;
    case 'primaryMarketEcoMap':
      formattedData = formatPrimaryMarket(data as PrimaryMarketBN);
      break;
    case 'primaryMarketGeneral':
      formattedData = formatPrimaryMarketGeneral(
        data as PrimaryMarketGeneralBN
      );
      break;
    case 'secondaryMarket':
      formattedData = formatSecondaryMarket(data as SecondaryMarketBN);
      break;
    case 'secondaryMarketGeneral':
      formattedData = formatSecondaryMarketGeneral(
        data as SecondaryMarketGeneralBN
      );
      break;
    case 'transactions':
      formattedData = formatTransactionedMarket(data as TransactionedMarketBN);
      break;
    case 'transactionsGeneral':
      formattedData = formatTransactionsMarketGeneral(
        data as TransactionedMarketBN
      );
      break;
    case 'socioeconomicDemographic':
      formattedData = data;
      break;
    case 'marketCycle':
      formattedData = formatMarketCycle(data as MarketCycleBN);
      periods = getPeriodsData(data as MarketCycleBN);
      break;
    default:
      formattedData = data;
      break;
  }
  return {
    formattedData,
    periods,
  };
};

const getPeriodsData = (
  data: MarketCycleBN
): Record<string, string | number> => {
  return {
    primary_percentual_oscillation:
      data?.primary_percentual_oscillation?.period,
    secondary_percentual_oscillation:
      data?.secondary_percentual_oscillation?.period,
    transactions_percentual_oscillation:
      data?.transactions_percentual_oscillation?.period,
  };
};

const formatPrimaryMarket = (data: PrimaryMarketBN): Record<string, string> => {
  const {
    avaliable_units,
    enterprises_count,
    sold_units_percentual,
    total_of_units,
    vgv,
  } = data;
  return {
    avaliable_units: labelFormatterNumber(avaliable_units),
    enterprises_count: labelFormatterNumber(enterprises_count),
    sold_units_percentual: `${sold_units_percentual}%`,
    total_of_units: labelFormatterNumber(total_of_units),
    vgv,
  };
};

const formatSecondaryMarket = (
  data: SecondaryMarketBN
): Record<string, string> => {
  const {
    average_construction_year,
    average_square_meter_price,
    existing_realties_amount,
    total_announced_realties,
  } = data;
  return {
    average_construction_year: labelFormatterNumber(average_construction_year),
    average_square_meter_price: labelFormatterMoneyAsInteger(
      Number(average_square_meter_price)
    ),
    existing_realties_amount: labelFormatterNumber(existing_realties_amount),
    total_announced_realties: labelFormatterNumber(total_announced_realties),
  };
};

const formatTransactionedMarket = (
  data: TransactionedMarketBN
): Record<string, string> => {
  const {
    existing_realties_count,
    existing_realties_median_age,
    transactioned_median_age,
    transactioned_realties_count,
  } = data;
  return {
    existing_realties_count: labelFormatterNumber(existing_realties_count),
    existing_realties_median_age: labelFormatterNumber(
      existing_realties_median_age
    ),
    transactioned_median_age: labelFormatterNumber(transactioned_median_age),
    // transactioned_realties_count: labelFormatterNumber(
    //   transactioned_realties_count
    // ),
  };
};

const formatMarketCycle = (
  data: MarketCycleBN
): Record<string, JSX.Element> => {
  const {
    primary_percentual_oscillation,
    secondary_percentual_oscillation,
    transactions_percentual_oscillation,
  } = data;
  const isNegative = (oscillation: MarketCycleBNData): boolean => {
    return oscillation?.value < 0;
  };
  const getJSX = (value: MarketCycleBNData): JSX.Element => {
    if (!value?.value || !value?.formattedValue) return <span>N/D</span>;
    return (
      <span>
        {isNegative(value) ? (
          <IcoArrowDownFilled
            cy={`BigNumbers`}
            style={{ marginRight: 8, color: '#D31717' }}
          />
        ) : (
          <IcoArrowUpFilled
            cy={`BigNumbers`}
            style={{ marginRight: 8, color: '#1D9A6C' }}
          />
        )}
        {value?.formattedValue.replaceAll('-', '')}
      </span>
    );
  };
  return {
    primary_percentual_oscillation: getJSX(primary_percentual_oscillation),
    secondary_percentual_oscillation: getJSX(secondary_percentual_oscillation),
    transactions_percentual_oscillation: getJSX(
      transactions_percentual_oscillation
    ),
  };
};

const formatPrimaryMarketGeneral = (
  data: PrimaryMarketGeneralBN
): Record<string, string> => {
  const { total_releases_ads, total_units, units_sold_percentage, stock } =
    data;
  return {
    total_releases_ads: labelFormatterNumber(total_releases_ads ?? 0),
  };
};

const formatSecondaryMarketGeneral = (
  data: SecondaryMarketGeneralBN
): Record<string, string | number> => {
  const {
    existent_real_estates_median_age,
    average_square_meter_price,
    total_ads,
    total_existent_real_estates,
  } = data;

  return {
    ...(existent_real_estates_median_age && {
      existent_real_estates_median_age: labelFormatterNumber(
        existent_real_estates_median_age
      ),
    }),
    average_square_meter_price,
    total_ads: labelFormatterNumber(total_ads),
    total_existent_real_estates: labelFormatterNumber(
      total_existent_real_estates
    ),
  };
};

const formatTransactionsMarketGeneral = (
  data: TransactionedMarketBN
): Record<string, string> => {
  const {
    existing_realties_count,
    existing_realties_median_age,
    transactioned_median_age,
    transactioned_realties_count,
  } = data;
  return {
    existing_realties_count: labelFormatterNumber(existing_realties_count),
    existing_realties_median_age: labelFormatterNumber(
      existing_realties_median_age
    ),
    transactioned_median_age: labelFormatterNumber(transactioned_median_age),
    // transactioned_realties_count: labelFormatterNumber(
    //   transactioned_realties_count
    // ),
  };
};
